import { X, Clock, Calendar, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import type { Caregiver } from '../../lib/types/data.types';
import type { CalendarEvent } from '../../lib/utils/calendar-helpers';
import { calculateAssignmentPhase } from '../../lib/utils/api-transforms';
import { useIsMobile } from '../../lib/hooks/use-mobile';

interface BookingDrawerProps {
  booking: CalendarEvent | null;
  caregiver: Caregiver | null;
  isOpen: boolean;
  onClose: () => void;
}

// Get status badge styling
function getStatusStyle(status: string): { bg: string; text: string; label: string } {
  switch (status) {
    case 'SCHEDULED':
      return { bg: '#dbeafe', text: '#1e40af', label: 'Scheduled' };
    case 'IN_PROGRESS':
      return { bg: '#dcfce7', text: '#15803d', label: 'In Progress' };
    case 'COMPLETED':
      return { bg: '#f3f4f6', text: '#374151', label: 'Completed' };
    case 'CANCELLED':
      return { bg: '#fee2e2', text: '#991b1b', label: 'Cancelled' };
    default:
      return { bg: '#f3f4f6', text: '#374151', label: status };
  }
}

// Get shift label
function getShiftLabel(shift: string): string {
  switch (shift) {
    case 'DAY':
      return 'Day Shift (8AM - 8PM)';
    case 'NIGHT':
      return 'Night Shift (8PM - 8AM)';
    case 'FULL':
      return 'Full Day (24 Hours)';
    default:
      return shift;
  }
}

export function BookingDrawer({ booking, caregiver, isOpen, onClose }: BookingDrawerProps) {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [assignmentPhase, setAssignmentPhase] = useState<{ currentDay: number; totalDays: number | null } | null>(null);

  // Animation effect
  useEffect(() => {
    if (isOpen) {
      // Mount and trigger enter animation
      setIsVisible(true);
      // Small delay to ensure DOM is ready for animation
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (isVisible) {
      // Trigger exit animation
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  // Fetch assignment phase when booking changes
  useEffect(() => {
    if (!booking || booking.type !== 'assignment' || !booking.carePlanId) {
      setAssignmentPhase(null);
      return;
    }

    // Fetch care plan assignment data
    const fetchAssignmentPhase = async () => {
      try {
        const { homeNursingService } = await import('../../api/homeNursingClient');
        const response = await homeNursingService.getCarePlanAssignment(booking.carePlanId!);

        if (response.data) {
          const phase = calculateAssignmentPhase(response.data, booking.startDate);
          setAssignmentPhase(phase);
        }
      } catch (error) {
        console.error('Failed to fetch assignment phase:', error);
        setAssignmentPhase(null);
      }
    };

    fetchAssignmentPhase();
  }, [booking]);

  if (!isVisible || !booking) return null;

  const statusStyle = getStatusStyle(booking.status || 'SCHEDULED');

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          zIndex: 40,
          opacity: isAnimating ? 1 : 0,
          transition: 'opacity 0.2s ease-out',
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          height: '100%',
          width: isMobile ? '100%' : '400px',
          backgroundColor: 'white',
          boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '16px' : '16px 24px',
          borderBottom: '1px solid #e4e4e7',
        }}>
          <h2 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#09090b', margin: 0 }}>
            Booking Details
          </h2>
          <button
            onClick={onClose}
            style={{
              width: isMobile ? '44px' : '32px',
              height: isMobile ? '44px' : '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            <X style={{ width: '20px', height: '20px', color: '#71717a' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: isMobile ? '20px 16px 16px 16px' : '24px 24px 16px 24px',
        }}>
          {/* Status Badge */}
          <div style={{ marginBottom: '20px' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 12px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor: statusStyle.bg,
              color: statusStyle.text,
            }}>
              {statusStyle.label}
            </span>
          </div>

          {/* Booking Title */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: 600,
              color: '#09090b',
              lineHeight: 1.2,
              margin: 0,
            }}>
              {booking.title}
            </h3>
            {booking.shift && (
              <p style={{
                fontSize: '14px',
                color: '#71717a',
                marginTop: '4px',
                marginBottom: 0,
              }}>
                {getShiftLabel(booking.shift)}
              </p>
            )}
          </div>

          {/* Nurse Info */}
          {caregiver && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              marginBottom: '24px',
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: '#dbeafe',
              }}>
                <img
                  src={caregiver.avatar}
                  alt={caregiver.fullName}
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                />
              </div>
              <div>
                <p style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#09090b',
                  margin: 0,
                }}>
                  {caregiver.fullName}
                </p>
                <p style={{
                  fontSize: '14px',
                  color: '#71717a',
                  margin: 0,
                }}>
                  {caregiver.city}
                </p>
              </div>
            </div>
          )}

          {/* Details Grid */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Date */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                backgroundColor: '#f4f4f5',
                flexShrink: 0,
              }}>
                <Calendar style={{ width: '16px', height: '16px', color: '#71717a' }} />
              </div>
              <div>
                <p style={{
                  fontSize: '12px',
                  color: '#71717a',
                  margin: 0,
                }}>
                  Date
                </p>
                <p style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#09090b',
                  margin: 0,
                }}>
                  {format(booking.startDate, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>

            {/* Time - Only show for assignments, not leaves */}
            {booking.type === 'assignment' && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  backgroundColor: '#f4f4f5',
                  flexShrink: 0,
                }}>
                  <Clock style={{ width: '16px', height: '16px', color: '#71717a' }} />
                </div>
                <div>
                  <p style={{
                    fontSize: '12px',
                    color: '#71717a',
                    margin: 0,
                  }}>
                    Time
                  </p>
                  <p style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#09090b',
                    margin: 0,
                  }}>
                    {booking.shift === 'DAY' ? '8:00 AM - 8:00 PM' :
                     booking.shift === 'NIGHT' ? '8:00 PM - 8:00 AM' :
                     '12:00 AM - 11:59 PM'}
                  </p>
                </div>
              </div>
            )}

            {/* Duration - Only show for assignments, not leaves */}
            {booking.type === 'assignment' && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  backgroundColor: '#f4f4f5',
                  flexShrink: 0,
                }}>
                  <Clock style={{ width: '16px', height: '16px', color: '#71717a' }} />
                </div>
                <div>
                  <p style={{
                    fontSize: '12px',
                    color: '#71717a',
                    margin: 0,
                  }}>
                    Duration
                  </p>
                  <p style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#09090b',
                    margin: 0,
                  }}>
                    {booking.hours || 12} hours
                  </p>
                </div>
              </div>
            )}

            {/* Assignment Phase - Only show for assignments with phase data */}
            {booking.type === 'assignment' && assignmentPhase && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  backgroundColor: '#f4f4f5',
                  flexShrink: 0,
                }}>
                  <FileText style={{ width: '16px', height: '16px', color: '#71717a' }} />
                </div>
                <div>
                  <p style={{
                    fontSize: '12px',
                    color: '#71717a',
                    margin: 0,
                  }}>
                    Assignment Phase
                  </p>
                  <p style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#09090b',
                    margin: 0,
                  }}>
                    Day {assignmentPhase.currentDay}{assignmentPhase.totalDays ? ` of ${assignmentPhase.totalDays}` : ''}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Event Type */}
          <div style={{
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: '1px solid #e4e4e7',
          }}>
            <p style={{
              fontSize: '13px',
              color: '#71717a',
              margin: '0 0 12px 0',
            }}>
              Event Type
            </p>
            <p style={{
              fontSize: '18px',
              fontWeight: 500,
              color: '#09090b',
              margin: 0,
            }}>
              {booking.type === 'assignment' ? 'Care Assignment' :
               booking.type === 'leave' ? 'Leave' :
               booking.type.charAt(0).toUpperCase() + booking.type.slice(1)}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: isMobile ? '12px 16px' : '12px 24px',
          borderTop: '1px solid #e4e4e7',
        }}>
          <p style={{
            fontSize: '12px',
            color: '#a1a1aa',
            textAlign: 'center',
            margin: 0,
          }}>
            Booking ID: {booking.id}
          </p>
        </div>
      </div>
    </>
  );
}
